/**
 * 영업시간 읽기 — **장소 상세와 플레이 모달이 같이 쓴다.**
 *
 * 원래 `place-detail-page-desktop/index.jsx` 안에만 있었는데, 플레이 모달의 「이 가게」 카드도
 * 같은 표시가 필요해졌다. 복사해 두면 한쪽만 고쳐져 갈라진다 —
 * 오늘 사이트맵과 페이지 색인 판정이 갈라져 있던 것과 같은 종류의 사고다. 그래서 여기 한 곳에 둔다.
 *
 * ⚠️ 요일 줄과 안내 줄을 나눠서 돌리는 이유, 첫 줄을 오늘로 치면 안 되는 이유는
 *    아래 주석에 그대로 옮겨 왔다. 지우지 말 것.
 */

const getHoursText = (place) =>
  place?.kakaoBusinessHours || place?.businessHours || "";

const getPrimaryHours = (hoursText) => {
  const firstLine = String(hoursText || "")
    .split("\n")
    .map((line) => line.trim())
    .find(Boolean);

  if (!firstLine) return "";

  const matched = firstLine.match(/(\d{1,2}:\d{2})\s*(?:~|-)\s*(\d{1,2}:\d{2})/);
  if (matched) return `오늘 ${matched[1]} - ${matched[2]}`;
  return firstLine;
};

// 영업시간은 원본 순서대로 나온다 — 실제로 "토·일·월·화·수·목·금" 으로 오는 곳이 많다.
// 문제는 화면이 **첫 줄을 오늘로 치고** 초록색으로 칠한다는 것이다(`index === 0 && isOpen`).
// 그래서 토요일 줄이 "지금 영업 중" 으로 칠해졌다. 오늘 요일을 맨 앞으로 돌려 놓으면
// 읽기도 자연스럽고 초록색도 맞는 줄에 간다.
const rotateToToday = (lines) => {
  const todayLabel = KOREAN_WEEKDAYS[new Date().getDay()];
  // ⚠️ 요일 줄과 안내 줄("- ※ 연중무휴 open ※")을 나눠서 돌린다.
  // 섞은 채로 돌리면 안내 줄이 요일 사이에 끼어, 뒤쪽 화면이 7줄만 그릴 때
  // 요일 하나가 잘려 나간다(실제로 월요일이 사라졌다).
  const dayLines = lines.filter((line) =>
    KOREAN_WEEKDAYS.some((day) => line.startsWith(day))
  );
  const noteLines = lines.filter(
    (line) => !KOREAN_WEEKDAYS.some((day) => line.startsWith(day))
  );
  const todayIndex = dayLines.findIndex((line) => line.startsWith(todayLabel));
  // 요일로 시작하지 않는 형태("매일 11:30-23:00")면 손대지 않는다.
  if (todayIndex < 0) return lines;
  return [
    ...dayLines.slice(todayIndex),
    ...dayLines.slice(0, todayIndex),
    ...noteLines,
  ];
};

const getBusinessHourLines = (hoursText) =>
  rotateToToday(
    String(hoursText || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
  );

const KOREAN_WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const getTimeMinutes = (time) => {
  const matched = String(time || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!matched) return null;
  return Number(matched[1]) * 60 + Number(matched[2]);
};

const getTodayBusinessStatus = (hoursText) => {
  const lines = getBusinessHourLines(hoursText);
  if (lines.length === 0) return null;

  const todayLabel = KOREAN_WEEKDAYS[new Date().getDay()];
  const todayLine =
    lines.find((line) => line.startsWith(todayLabel)) ||
    lines.find((line) => line.startsWith("매일"));
  if (!todayLine) return null;

  const matched = todayLine.match(/(\d{1,2}:\d{2})\s*(?:~|-)\s*(\d{1,2}:\d{2})/);
  if (!matched) return null;

  const openMinutes = getTimeMinutes(matched[1]);
  const closeMinutes = getTimeMinutes(matched[2]);
  if (openMinutes === null || closeMinutes === null) return null;

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const isOvernight = closeMinutes <= openMinutes;
  const isOpen = isOvernight
    ? currentMinutes >= openMinutes || currentMinutes <= closeMinutes
    : currentMinutes >= openMinutes && currentMinutes <= closeMinutes;

  return {
    isOpen,
    closeTime: matched[2],
  };
};

const getClosingTime = (hoursText) => {
  const matched = String(hoursText || "").match(/(\d{1,2}:\d{2})\s*(?:~|-)\s*(\d{1,2}:\d{2})/);
  return matched?.[2] || "";
};

export {
  getHoursText,
  getPrimaryHours,
  rotateToToday,
  getBusinessHourLines,
  getTodayBusinessStatus,
  getClosingTime,
  KOREAN_WEEKDAYS,
};

/** 「영업중 · 21:00 종료」 한 줄. 닫혀 있거나 알 수 없으면 빈 문자열. */
export const getOpenStatusText = (place) => {
  const hoursText = getHoursText(place);
  const status = getTodayBusinessStatus(hoursText);
  if (!status?.isOpen) return "";
  const closeTime = status.closeTime || getClosingTime(hoursText);
  return closeTime ? `영업중 · ${closeTime} 종료` : "영업중";
};
